import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog.js";
import { Input } from "../ui/input.js";
import { Button } from "../ui/button.js";

interface AvailableRepo {
  owner: string;
  name: string;
  fullName: string;
  defaultBranch: string;
  added: boolean;
}

interface RepoPickerModalProps {
  open: boolean;
  onClose: () => void;
  onReposChanged: () => void;
}

/** GitHub names are case-insensitive, and the workspace keeps the case it was added with. */
function sameRepo(a: { owner: string; name: string }, b: { owner: string; name: string }): boolean {
  return a.owner.toLowerCase() === b.owner.toLowerCase() && a.name.toLowerCase() === b.name.toLowerCase();
}

export function RepoPickerModal({ open, onClose, onReposChanged }: RepoPickerModalProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [repos, setRepos] = useState<AvailableRepo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The environment belongs to the repository: every workspace opens it on
  // the repositories page, whatever Fletcher's flag says.
  const handleOpenEnvironment = useCallback(async (repo: AvailableRepo) => {
    const listRes = await fetch("/api/repos", { credentials: "include" });
    if (!listRes.ok) return;
    const workspaceRepos = (await listRes.json()) as Array<{
      id: string;
      owner: string;
      name: string;
    }>;
    const match = workspaceRepos.find(
      (r) => sameRepo(r, repo),
    );
    if (!match) return;
    onClose();
    void navigate(`/services/repositories?repo=${encodeURIComponent(match.id)}`);
  }, [navigate, onClose]);

  const fetchRepos = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : "";
      const res = await fetch(`/api/repos/available${params}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null;
        setError(body?.error ?? "Failed to load repos");
        setRepos([]);
        return;
      }
      const data = (await res.json()) as { repos: AvailableRepo[]; totalCount: number };
      setRepos(data.repos);
    } catch {
      setError("Failed to connect");
      setRepos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open
  useEffect(() => {
    if (open) {
      void fetchRepos(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchRepos(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, fetchRepos]);

  const handleAdd = useCallback(async (repo: AvailableRepo) => {
    setActionPending((prev) => new Set(prev).add(repo.fullName));
    try {
      const res = await fetch("/api/repos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          owner: repo.owner,
          name: repo.name,
          defaultBranch: repo.defaultBranch,
        }),
      });
      if (res.ok) {
        setRepos((prev) =>
          prev.map((r) => (r.fullName === repo.fullName ? { ...r, added: true } : r)),
        );
        onReposChanged();
      }
    } finally {
      setActionPending((prev) => {
        const next = new Set(prev);
        next.delete(repo.fullName);
        return next;
      });
    }
  }, [onReposChanged]);

  const handleRemove = useCallback(async (repo: AvailableRepo) => {
    setActionPending((prev) => new Set(prev).add(repo.fullName));
    try {
      // Fetch workspace repos to find the ID for this repo
      const listRes = await fetch("/api/repos", { credentials: "include" });
      if (!listRes.ok) return;
      const workspaceRepos = (await listRes.json()) as Array<{ id: string; owner: string; name: string }>;
      const match = workspaceRepos.find((r) => sameRepo(r, repo));
      if (!match) return;

      const res = await fetch(`/api/repos/${match.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setRepos((prev) =>
          prev.map((r) => (r.fullName === repo.fullName ? { ...r, added: false } : r)),
        );
        onReposChanged();
      }
    } finally {
      setActionPending((prev) => {
        const next = new Set(prev);
        next.delete(repo.fullName);
        return next;
      });
    }
  }, [onReposChanged]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        aria-label="Select repositories"
        className="max-w-md flex flex-col p-0"
        style={{ maxHeight: "70vh" }}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <DialogTitle className="text-sm font-semibold mb-3">
            Select Repositories
          </DialogTitle>
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search repositories..."
            inputSize="sm"
            autoFocus
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0">
          {error ? (
            <div className="text-center py-8">
              <p className="text-sm text-text-secondary mb-3">{error}</p>
              <div className="flex items-center justify-center gap-4">
                <a
                  href="/api/auth/github/install?return_to=/services"
                  className="text-xs font-medium text-accent hover:text-accent-hover"
                >
                  Reconnect GitHub
                </a>
                <a
                  href="/api/auth/github/install?mode=authorize&return_to=/services"
                  className="text-xs font-medium text-accent hover:text-accent-hover"
                  title="YAGNI is already installed on GitHub but not linked to this workspace"
                >
                  Link existing
                </a>
              </div>
            </div>
          ) : loading && repos.length === 0 ? (
            <div className="text-center py-8 text-sm text-text-tertiary">
              Loading...
            </div>
          ) : repos.length === 0 ? (
            <div className="text-center py-8 text-sm text-text-tertiary">
              No repos found{query ? " matching your search" : ""}
            </div>
          ) : (
            <div className="space-y-1">
              {repos.map((repo) => {
                const pending = actionPending.has(repo.fullName);
                return (
                  <div
                    key={repo.fullName}
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-[rgba(var(--glass-rgb),0.68)]"
                  >
                    <span className="text-sm text-text-primary truncate mr-3">
                      {repo.fullName}
                    </span>
                    {repo.added ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-success font-medium">Added</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleOpenEnvironment(repo)}
                          disabled={pending}
                          className="text-xs h-auto px-sm py-xs"
                        >
                          Environment
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleRemove(repo)}
                          disabled={pending}
                          className="text-xs h-auto px-sm py-xs hover:text-error"
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => void handleAdd(repo)}
                        disabled={pending}
                        className="text-xs flex-shrink-0"
                      >
                        Add
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
